import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import ShareIcon from "@mui/icons-material/Share";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";

import { useProject } from "../api/project";
import { useAuth } from "../contexts/AuthContext";
import { useSimulations } from "../api/simulations";
import { useStrategies } from "../api/strategies";
import {
  useCreateStoryMap,
  useDeleteStoryMap,
  useStoryMaps,
} from "../api/storyMaps";
import { api } from "../utils/api";
import { ApiResponse } from "../types/api";
import { StoryContent, StoryMap, StoryMapSummary } from "../types/storyMap";
import ShareDialog from "../components/storyMap/ShareDialog";
import NewStoryDialog from "../components/storyMap/NewStoryDialog";
import { StoryTemplateContext } from "../components/storyMap/storyTemplates";
import { defaultStoryContent } from "../components/storyMap/storyDefaults";
import { coverStaticMapUrl } from "../components/storyMap/storyLayers";

/** Tile cover: static map of the first scene camera, else first story image. */
const coverUrl = (story: StoryMapSummary): string | null => {
  if (story.cover?.camera?.center) return coverStaticMapUrl(story.cover.camera);
  return story.cover?.image_url ?? null;
};

const TEAL = "#00d4ad";
const SLATE = "#212b37";

export default function StoryMapsPage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useProject();
  const { user } = useAuth();

  const { data, isLoading } = useStoryMaps(projectId);
  const stories = data?.data ?? [];

  const createStory = useCreateStoryMap(projectId);
  const deleteStory = useDeleteStoryMap();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuStory, setMenuStory] = useState<StoryMapSummary | null>(null);
  const [shareStory, setShareStory] = useState<StoryMapSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StoryMapSummary | null>(null);
  const [newStoryOpen, setNewStoryOpen] = useState(false);

  // Template inputs. Fetched here rather than in the dialog so the tiles know
  // up front which templates this project can actually satisfy.
  const { strategies } = useStrategies(Number(projectId));
  const { data: simsData } = useSimulations();

  const templateContext: StoryTemplateContext = useMemo(
    () => ({
      authorName: user?.username,
      scope: "project" as const,
      projectName: project?.data?.name,
      boundary: project?.data?.boundary ?? null,
      strategies: (strategies?.data ?? []).map((s) => ({ id: s.id, name: s.name })),
      simulations: (simsData?.data ?? [])
        .filter((s) => !!s.simulation_id && s.status === "SUCCESS")
        .map((s) => ({
          simulation_id: s.simulation_id,
          name: s.name,
          is_ensemble: s.is_ensemble,
        })),
      today: new Date().toISOString().slice(0, 10),
      unitSystem: user?.unit_system ?? "metric",
    }),
    [user?.username, user?.unit_system, project, strategies, simsData]
  );

  const handleCreateFromTemplate = (title: string, content: StoryContent) => {
    createStory.mutate(
      { title, content },
      {
        onSuccess: (response) => {
          setNewStoryOpen(false);
          navigate(`/projects/${projectId}/story-maps/${response.data.id}`);
        },
      }
    );
  };

  const handleDuplicate = async (story: StoryMapSummary) => {
    // Summary rows don't carry content — fetch the full story first.
    const detail = await api.request<ApiResponse<StoryMap>>(
      `/api/story-maps/${story.id}`
    );
    createStory.mutate({
      title: `Copy of ${story.title}`,
      content: detail.data.content ?? defaultStoryContent(user?.username),
    });
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuStory(null);
  };

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

  return (
    <Box sx={{ minHeight: "calc(var(--vh100) - var(--topbar-h))", bgcolor: "#f4f6f8" }}>
      {/* Header bar (design 1a) */}
      <Box
        sx={{
          bgcolor: SLATE,
          color: "white",
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          gap: 1,
        }}
      >
        <IconButton onClick={() => navigate(`/projects/${projectId}`)} sx={{ color: "white" }}>
          <ArrowBackIcon />
        </IconButton>
        <AutoStoriesIcon sx={{ color: TEAL }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {project?.data?.name ?? "Project"} | Story Maps
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AutoStoriesIcon />}
          onClick={() => setNewStoryOpen(true)}
          disabled={createStory.isPending}
          sx={{
            bgcolor: TEAL,
            color: SLATE,
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { bgcolor: "#00bd9a" },
          }}
        >
          New story
        </Button>
      </Box>

      <Box sx={{ maxWidth: 1160, mx: "auto", px: 3, py: 3 }}>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
          Share project findings as interactive narratives — frozen at publish,
          safe to circulate.
        </Typography>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress color="secondary" />
          </Box>
        )}

        {/* First run: say what a story is and what the first move is, rather
            than showing a lone dashed tile against an empty page. */}
        {!isLoading && stories.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              mb: 3,
              textAlign: "center",
              borderStyle: "dashed",
              borderWidth: 2,
            }}
          >
            <AutoStoriesIcon sx={{ color: TEAL, fontSize: 40 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
              No stories in this project yet
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 1, mx: "auto", maxWidth: 560 }}
            >
              A story map is a series of scenes. Readers scroll, and the map
              moves with them — through treatments, fuel state and simulation
              results from this project. Publishing freezes a copy, so a link
              you send never changes underneath the reader.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AutoStoriesIcon />}
              onClick={() => setNewStoryOpen(true)}
              sx={{
                mt: 2.5,
                bgcolor: TEAL,
                color: SLATE,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "#00bd9a" },
              }}
            >
              Start from a template
            </Button>
          </Paper>
        )}

        {/* Responsive tile grid (design 1a): ~3 across on desktop */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2,
          }}
        >
          {stories.map((story) => (
            <Paper
              key={story.id}
              variant="outlined"
              sx={{
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                overflow: "hidden",
                "&:hover": { borderColor: TEAL },
              }}
              onClick={() => navigate(`/projects/${projectId}/story-maps/${story.id}`)}
            >
              {/* Cover: first scene camera as a static map, else first image */}
              {coverUrl(story) ? (
                <Box
                  component="img"
                  src={coverUrl(story) as string}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: 120,
                    objectFit: "cover",
                    display: "block",
                    bgcolor: SLATE,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${SLATE} 0%, #2b3949 100%)`,
                  }}
                >
                  <AutoStoriesIcon sx={{ color: TEAL, opacity: 0.7, fontSize: 36 }} />
                </Box>
              )}
              <Box
                sx={{
                  p: 2,
                  pt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flexGrow: 1,
                }}
              >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                    {story.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {story.scene_count} section{story.scene_count === 1 ? "" : "s"} · Updated{" "}
                    {formatDate(story.updated_at)}
                    {story.created_by_name ? ` · ${story.created_by_name}` : ""}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {story.status === "PUBLISHED" ? (
                  <Chip
                    icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                    label={`${story.view_count} view${story.view_count === 1 ? "" : "s"}`}
                    size="small"
                    sx={{ bgcolor: "rgba(0, 212, 173, 0.15)", color: "#00806a", fontWeight: 600 }}
                  />
                ) : story.status === "PUBLISHING" ? (
                  // The freeze runs on a worker, so this survives a refresh
                  // and is visible to everyone in the tenant, not just the
                  // tab that started it.
                  <Chip
                    icon={<CircularProgress size={12} sx={{ color: "#8a5a00" }} />}
                    label={story.publish_detail || "Publishing…"}
                    size="small"
                    sx={{ bgcolor: "rgba(224,165,74,0.18)", color: "#8a5a00", fontWeight: 600 }}
                  />
                ) : story.status === "PUBLISH_FAILED" ? (
                  <Chip
                    label="Publishing failed"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                ) : (
                  <Chip label="Not yet shared" size="small" sx={{ color: "text.secondary" }} />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Share" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareStory(story);
                    }}
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor(e.currentTarget);
                    setMenuStory(story);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              </Box>
            </Paper>
          ))}

          {/* Dashed "start a new story" tile. Suppressed on first run — the
              empty state above already carries the call to action. */}
          {stories.length > 0 && (
            <Paper
              variant="outlined"
              onClick={() => setNewStoryOpen(true)}
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                borderStyle: "dashed",
                borderWidth: 2,
                color: "text.secondary",
                cursor: "pointer",
                minHeight: 148,
                "&:hover": { borderColor: TEAL, color: "#00806a" },
              }}
            >
              <AddIcon />
              <Typography variant="subtitle1">Start a new story</Typography>
              <Typography variant="caption">From a template, or blank</Typography>
            </Paper>
          )}
        </Box>
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuStory) navigate(`/projects/${projectId}/story-maps/${menuStory.id}`);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuStory) handleDuplicate(menuStory);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteTarget(menuStory);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete “{deleteTarget?.title}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            This permanently deletes the story{deleteTarget?.status === "PUBLISHED" ? " and its shared link" : ""}, along with
            everything it published — frozen map data, uploaded images and the
            PDF. Those files stay reachable to anyone holding their URLs until
            the story is deleted, so this is the only way to retract them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={() => {
              if (deleteTarget) deleteStory.mutate(deleteTarget.id);
              setDeleteTarget(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {shareStory && (
        <ShareDialog
          open
          onClose={() => setShareStory(null)}
          story={shareStory}
          scopeLabel={project?.data?.name ? `${project.data.name} project` : null}
        />
      )}

      <NewStoryDialog
        open={newStoryOpen}
        onClose={() => setNewStoryOpen(false)}
        context={templateContext}
        onCreate={handleCreateFromTemplate}
        creating={createStory.isPending}
      />
    </Box>
  );
}
